import { Building2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ScopePicker } from "@/components/layout/ScopePicker";
import { useEnergyAdvisorCustomers } from "@/hooks/useEnergyAdvisorCustomers";
import { setAdvisorClient } from "@/lib/advisor-client-context";

/**
 * The Energy Advisor module's client picker.
 *
 * **Why a picker instead of a clients list page.** Every Energy Advisor
 * surface is client-scoped, so the client is the module's *scope*, not a
 * destination inside it. Modeling it as a list page meant travelling to a
 * page to change scope, and left the nav claiming a client while you were on
 * that page choosing one. As a header control it sits one scope below the
 * module switcher — module › client — stays put on every page (including
 * Accounts, Clients, and Deals), and is the single way to change client.
 * Salesforce's app+record scoping and Azure's subscription picker have the
 * same shape.
 *
 * Selecting a client both records the selection (`setAdvisorClient`, so it
 * survives navigating to a shared record and re-entering the module) and
 * navigates into that client, so one action moves you rather than only
 * arming the nav.
 *
 * The chrome lives in `ScopePicker`, shared with Market Intelligence's region
 * picker. What stays here is what is specific to clients: the unbounded
 * server-side search, and recording the selection.
 */
export function AdvisorClientPicker({
  /** The client in scope, or `null` when none is chosen yet. */
  selected,
  /** Where selecting a client navigates: `${basePath}/${id}`. */
  basePath,
  placeholder,
  /** Closes the mobile drawer after a selection. */
  onClose,
}: {
  selected: { id: string; label: string } | null;
  basePath: string;
  placeholder: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  // Server-side search rather than `Command`'s client-side filter: the client
  // list is unbounded (hundreds per tenant), so filtering a fetched page
  // would silently hide matches that aren't on it. Passing `onSearch` is what
  // hands filtering to the query.
  const [query, setQuery] = useState("");
  const { data, isLoading } = useEnergyAdvisorCustomers({
    q: query || undefined,
    sort: "name",
  });

  return (
    <ScopePicker
      axis="Client"
      icon={Building2}
      selected={selected}
      placeholder={placeholder}
      searchPlaceholder="Search clients…"
      emptyMessage="No clients match."
      options={(data?.items ?? []).map((c) => ({ id: c.id, label: c.name }))}
      isLoading={isLoading}
      onSearch={setQuery}
      onSelect={(id) => {
        setAdvisorClient(id);
        onClose();
        navigate(`${basePath}/${id}`);
      }}
    />
  );
}
